// drugResponseSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { getApiErrorMessage } from 'src/types/api';

export interface DrugItem {
  B_corr_clipped: number;
  A_nrmse?: number;
  score?: number;
  drug_name?: string;
  pathway_name?: string;
  targets?: string;
}

interface DrugResponse {
  drug_list: DrugItem[];
}

export interface iDrugScoreState {
  response: DrugResponse | null;
  loading: boolean;
  error: string | null;
}

const initialState: iDrugScoreState = {
  response: null,
  loading: false,
  error: null,
};

// CSV 라인 파싱 함수 (따옴표 안의 쉼표 처리)
const parseLine = (line: string): string[] => {
  const result: string[] = [];
  let current = '';
  let insideQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    
    if (char === '"') {
      insideQuotes = !insideQuotes;
    } else if (char === ',' && !insideQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  result.push(current.trim());
  
  return result;
};

// CSV를 객체 배열로 파싱
const parseCSVToObjects = (text: string): DrugItem[] => {
  const lines = text.split('\n').filter(line => line.trim());
  
  if (lines.length === 0) {
    console.warn('⚠️ CSV 파일이 비어있습니다.');
    return [];
  }
  
  // 헤더 파싱
  const headers = parseLine(lines[0]);
  
  // 각 컬럼의 인덱스 찾기
  const getColumnIndex = (columnName: string) => headers.indexOf(columnName);
  
  const indices = {
    B_corr_clipped: getColumnIndex('B_corr_clipped'),
    A_nrmse: getColumnIndex('A_nrmse'),
    score: getColumnIndex('score'),
    drug_name: getColumnIndex('drug_name'),
    pathwayName: getColumnIndex('pathway_name'),
    targets: getColumnIndex('targets'),
  };
  
  
  // 필수 컬럼 체크
  if (indices.B_corr_clipped === -1) {
    console.error('❌ "drug_name" 컬럼을 찾을 수 없습니다!');
    return [];
  }
  
  // 데이터 파싱
  const dataList = lines
    .slice(1) // 헤더 제거
    .map((line, index) => {
      const values = parseLine(line);
      
      const item: DrugItem = {
        B_corr_clipped: parseFloat(values[indices.B_corr_clipped]) || NaN,
        A_nrmse: indices.A_nrmse !== -1 ? parseFloat(values[indices.A_nrmse]) || undefined : undefined,
        score: indices.score !== -1 ? parseFloat(values[indices.score]) || undefined : undefined,
        drug_name: indices.drug_name !== -1 ? values[indices.drug_name] : undefined,
        pathway_name: indices.pathwayName !== -1 ? values[indices.pathwayName] : undefined,
        targets: indices.targets !== -1 ? values[indices.targets] : undefined,
      };
      
      return item;
    })
    .filter(item => !isNaN(item.B_corr_clipped)); // 유효한 데이터만
  
  
  return dataList;
};

// CSV에서 Drug 리스트 가져오기
export const getDrugScoreDataAPI = createAsyncThunk<DrugResponse>(
  'drugScoreResponse/getFromCSV',
  async (_, { rejectWithValue }) => {
    try {
      
      const response = await fetch('/data/drug_score_results.csv');
      
      if (!response.ok) {
        throw new Error(`CSV 파일 로드 실패: ${response.status} ${response.statusText}`);
      }
      
      const text = await response.text();

      
      const drugList = parseCSVToObjects(text);
      
      if (drugList.length === 0) {
        throw new Error('파싱된 데이터가 없습니다.');
      }
      
      
      return { drug_list: drugList };
    } catch (error) {
      console.error('Drug List CSV 로딩 실패:', error);
      return rejectWithValue(getApiErrorMessage(error, 'Failed to load drug list'));
    }
  }
);

const drugScoreSlice = createSlice({
  name: 'drugResponse',
  initialState,
  reducers: {
    clearDrugScoreList: (state) => {
      state.response = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDrugScoreDataAPI.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getDrugScoreDataAPI.fulfilled, (state, action: PayloadAction<DrugResponse>) => {
        state.loading = false;
        state.response = action.payload;
      })
      .addCase(getDrugScoreDataAPI.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Failed to load drug list';
        console.error(' Redux 에러:', action.payload);
      });
  },
});

export const { clearDrugScoreList } = drugScoreSlice.actions;
export default drugScoreSlice.reducer;